import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useEffect, useRef } from 'react';

import {
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
} from '@/atoms/chronicle';
import { useMutatePatchDeviceFirmware } from '@/hooks/mutation/desktop/chronicle/useMutatePatchDeviceFirmware';
import { chronicleBridge } from '@/tauri/chronicle';
import {
  listenChronicleOtaCompleted,
  listenChronicleOtaFailed,
  listenChronicleOtaState,
} from '@/tauri/chronicle/events';
import {
  trackFirmwareUpgradeCompleted,
  trackFirmwareUpgradeFailed,
} from '@/utils/analytics/track-ota-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

/** Only subscribe to the fields needed for OTA analytics to avoid re-renders on unrelated state changes. */
const otaAnalyticsFieldsAtom = selectAtom(chronicleDeviceStateAtom, (state) => ({
  deviceId: state.deviceId,
  deviceName: state.deviceName,
  latestFirmwareVersion: state.latestFirmwareVersion,
}));

/** Subscribe only to the BE document id — avoids re-renders on unrelated connection-context changes. */
const linkedDeviceIdAtom = selectAtom(
  chronicleConnectionContextAtom,
  (ctx) => ctx.linkedDeviceId
);

/**
 * Subscribe only to firmwareVersion — drives the post-OTA backend-sync gate.
 *
 * We watch this field directly (rather than the `connected` state transition)
 * because Rust clears `firmware_version` on reconnect start and only re-fills
 * it after the device responds to `MCU&FW&` — which arrives in a SEPARATE
 * state event AFTER the `connected` event. A `connected` transition would
 * fire too early, before the real new version is known.
 */
const firmwareVersionAtom = selectAtom(
  chronicleDeviceStateAtom,
  (state) => state.firmwareVersion
);

export function useSetupChronicleOtaEvents() {
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const analyticsFields = useAtomValue(otaAnalyticsFieldsAtom);
  const analyticsFieldsRef = useRef(analyticsFields);
  analyticsFieldsRef.current = analyticsFields;

  // Ref-tracked so the firmware-watcher effect below can read the latest
  // linkedDeviceId without taking it as a dependency — keeps that effect
  // keyed on firmwareVersion changes only.
  const linkedDeviceId = useAtomValue(linkedDeviceIdAtom);
  const linkedDeviceIdRef = useRef(linkedDeviceId);
  linkedDeviceIdRef.current = linkedDeviceId;

  // Read at `ota_completed` time via the ref so the gate captures the
  // pre-OTA value before the post-reconnect MCU&FW& update overwrites it.
  const firmwareVersion = useAtomValue(firmwareVersionAtom);
  const firmwareVersionRef = useRef(firmwareVersion);
  firmwareVersionRef.current = firmwareVersion;

  // `.mutate` is a stable reference from react-query — safe to capture once
  // in the listener closure below; no ref wrapper needed.
  const { mutate: patchFirmwareMutate } = useMutatePatchDeviceFirmware();

  // Single-use gate. Armed when `ota_completed` fires (regardless of catalog
  // vs local-file source — the OTA target version isn't available for
  // local-file picks, so we always wait for the post-reconnect firmware
  // report). Consumed when the firmwareVersion atom first changes to a
  // different non-null value after arming. Mirrors iOS APP-4922.
  const otaPatchGateRef = useRef<{ armed: boolean; preOtaVersion: string | null }>({
    armed: false,
    preOtaVersion: null,
  });

  useEffect(() => {
    if (!chronicleBridge.isAvailable()) return;

    let cancelled = false;
    const unlisteners: (() => void)[] = [];

    const setup = async () => {
      try {
        const unlistenState = await listenChronicleOtaState((event) => {
          const { status, progress } = event.payload;
          logger.debug({ msg: 'Chronicle OTA state event', status, progress });

          const isActive =
            status !== 'idle' &&
            status !== 'completed' &&
            status !== 'failed' &&
            status !== 'cancelled';

          setDeviceState((prev) => ({
            ...prev,
            firmwareUpdateInProgress: isActive,
            firmwareUpdateStatus: status,
            firmwareUpdateProgress: progress !== null ? Math.round(progress * 100) : null,
            // Only clear error when transitioning to an active OTA state.
            // Preserve existing error during idle/failed/cancelled so the UI can read it.
            firmwareUpdateError: isActive ? null : prev.firmwareUpdateError,
          }));
        });

        if (cancelled) {
          unlistenState();
          return;
        }
        unlisteners.push(unlistenState);

        const unlistenCompleted = await listenChronicleOtaCompleted((event) => {
          logger.info({ msg: 'Chronicle OTA completed' });

          const { deviceId, deviceName, latestFirmwareVersion } =
            analyticsFieldsRef.current;
          const targetVersion = event.payload.targetVersion?.trim() || null;
          trackFirmwareUpgradeCompleted({
            deviceId,
            deviceName,
            targetFirmwareVersion: targetVersion ?? latestFirmwareVersion,
          });

          // Close out the OTA progress UI. We deliberately don't touch
          // `firmwareVersion` or the catalog banner here — the BLE reconnect
          // delivers the actually-booted version via map-device-state, and
          // once the BE PATCH below lands, app-relaunch is also correct.
          setDeviceState((prev) => ({
            ...prev,
            firmwareUpdateInProgress: false,
            firmwareUpdateStatus: 'completed',
            firmwareUpdateProgress: 100,
            firmwareUpdateError: null,
            selectedFirmwareFile: null,
          }));

          // Arm the post-reconnect backend-sync gate. The PATCH itself
          // fires later in the connectionStatus-watching effect below, when
          // the device reconnects with the actually-booted firmware version
          // (uniform across catalog + local-file OTAs; survives rollback).
          // Mirrors iOS APP-4922.
          otaPatchGateRef.current = {
            armed: true,
            preOtaVersion: firmwareVersionRef.current,
          };
        });

        if (cancelled) {
          unlistenCompleted();
          return;
        }
        unlisteners.push(unlistenCompleted);

        const unlistenFailed = await listenChronicleOtaFailed((event) => {
          const { errorCode, errorMessage, retryable } = event.payload;
          logger.error({
            msg: 'Chronicle OTA failed',
            errorCode,
            errorMessage,
          });
          const { deviceId, deviceName, latestFirmwareVersion } =
            analyticsFieldsRef.current;
          trackFirmwareUpgradeFailed({
            deviceId,
            deviceName,
            targetFirmwareVersion: latestFirmwareVersion,
            errorCode,
            errorMessage,
            isRetryable: retryable,
          });

          setDeviceState((prev) => ({
            ...prev,
            firmwareUpdateInProgress: false,
            firmwareUpdateStatus: 'failed',
            firmwareUpdateProgress: null,
            firmwareUpdateError: {
              code: errorCode,
              message: errorMessage,
              retryable,
            },
          }));
        });

        if (cancelled) {
          unlistenFailed();
          return;
        }
        unlisteners.push(unlistenFailed);
      } catch (error) {
        logger.error({
          msg: 'Failed to setup Chronicle OTA event listeners',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void setup();

    return () => {
      cancelled = true;
      for (const unlisten of unlisteners) {
        unlisten();
      }
    };
  }, [setDeviceState, patchFirmwareMutate]);

  // Post-OTA backend sync — fires when the firmwareVersion atom changes to a
  // new non-null value AFTER `ota_completed` armed the gate. This is the
  // signal that the device's MCU&FW& response has landed post-reconnect
  // (the `connected` state transition itself happens earlier and carries no
  // version yet — see the firmwareVersionAtom comment above).
  //
  // Single-use: the first valid post-arm change consumes the gate, even if
  // we end up skipping the PATCH (e.g. linkedDeviceId is null) — preventing
  // a later unrelated firmwareVersion change from re-entering the path.
  useEffect(() => {
    if (!otaPatchGateRef.current.armed) return;
    if (firmwareVersion == null) return;
    if (firmwareVersion === otaPatchGateRef.current.preOtaVersion) return;

    otaPatchGateRef.current = { armed: false, preOtaVersion: null };

    const id = linkedDeviceIdRef.current;
    if (id == null) {
      logger.info({
        msg: 'Post-OTA firmware change detected but linkedDeviceId is null; skipping backend PATCH',
        firmwareVersion,
      });
      return;
    }

    patchFirmwareMutate({ id, firmwareVersion });
  }, [firmwareVersion, patchFirmwareMutate]);
}
